Privacy Policy

FieldOps Notes

*Effective Date: June 1, 2026*

FieldOps Notes, Inc. d/b/a FieldOps Notes ("FieldOps Notes," "we," "our," and "us") respects your concerns about privacy and personal data protection and values its relationship with you.

This Privacy Policy (this "Policy") applies to personal information collected through the FieldOps Notes mobile application and fieldopsai.team (together, the "Services"), whether accessed by computer, mobile device, or another supported device. The Services are used by authorized organizations and their canvassers to conduct canvassing activity, capture and process field information from doorstep interactions, document visits, and review canvassing activity.

This Policy describes the types of personal information we collect through the Services — including information collected through your device's microphone and GPS/location features — and how that personal information may be used and/or with whom it may be shared. This Policy also describes how you can reach us to update your personal information, access and control the use of your personal information, or get answers to questions you may have about our privacy practices. Please read this Policy carefully, because by accessing and using the Services you are acknowledging that you understand and agree to the terms of this Policy.

This Privacy Policy does not apply to any websites, applications, or services operated by third parties. We are not responsible for, and this Policy does not apply to, the privacy practices of other companies. We encourage you to seek out and read the privacy policy of each third-party service you use.

# **Table of Contents**

1\. How do we collect your information?

2\. What information do we collect?

3\. How do we use your personal information?

4\. Do we disclose any information to outside parties?

5\. What choices do you have about your personal information?

6\. Cookies

7\. Location of this Site

8\. How long do we retain your personal information?

9\. Third party links

10\. Third party application usage policy

11\. Automated Transcription and Language-Processing Services

12\. How do we protect your information?

13\. Your California privacy rights

14\. Do we collect information from children?

15\. Contacting us

16\. Changes to our Privacy Policy

# 1. How do we collect your information?

FieldOps Notes collects information relating to you and your use of the Services to provide canvassing-session capture, field documentation, and management functionality.

**From you.**

We collect information when you sign in, start or end a canvassing session, capture a doorstep interaction, submit information through the Services, contact support, or otherwise interact with the Services.

**Automatically through your device.**

When you use the mobile application, we automatically collect certain information from your device, including precise GPS location, microphone audio during an active canvassing session, device identifiers, application diagnostics, and usage data, as described in Section 2.

**From your organization and integrated sources.**

We may receive information from the organization that authorizes your use of the Services, from voter or household data sources used by that organization, from systems integrated with the Services, and from service providers that help us operate the Services.

# **2\. What information do we collect?**

**Microphone and Audio Data.**

With your permission, FieldOps Notes uses your device microphone to record audio during a canvassing session that you start. Recording may continue while the application is in the background or the screen is off until the session is ended, recovered, or automatically ended under the application's session controls. Audio may be processed by automated speech-to-text and language-processing systems to produce transcripts, summaries, sentiments, observations, structured visit notes, and related canvassing documentation. FieldOps Notes does not begin microphone capture outside a user-initiated canvassing session.

**GPS and Location Data.**

With your permission, FieldOps Notes collects precise location information during a canvassing session, including while the application is in the background or the screen is off. The Services use this information to record the canvassing route, associate activity with a session or visit, support visit documentation, and allow the authorized organization to review field activity. Location information may be visible to authorized users of your organization. FieldOps Notes does not begin route capture outside a user-initiated canvassing session.

**Account and Organization Information.**

Name, email address, phone number, role, organization or brokerage affiliation, login credentials, and identifiers needed to associate your account with your authorized organization.

**Canvassing Session and Route Information.**

Session identifiers, canvasser identity, session start and end times, session status, GPS route points, visit timing, capture and processing status, and related operational records.

**Doorstep Interaction and Field Documentation.**

Conversation audio, transcripts, summaries, sentiments, observations, structured field notes, addresses or visit locations, and issue or policy-position information recorded or derived from a doorstep interaction.

**Household and Voter Information.**

Where supplied or authorized by your organization, household and voter identifiers, names, addresses, phone information, and related canvassing records used to support field activity and reporting.

**Device and Usage Information.**

Device type, operating system, application version, IP address, crash logs, diagnostics, and general usage information collected while you use the Services.

**Cookies and Similar Technologies.**

If you access our website, we may use cookies and similar technologies to operate the site, remember preferences, and understand site usage, as described in Section 6 below.

# **3\. How do we use your personal information?**

We use personal information collected through the Services to:

- authenticate users and associate them with an authorized organization;
- start, maintain, recover, and end canvassing sessions;
- capture, process, and document doorstep interactions and field activity;
- transcribe and process audio and generate summaries, sentiments, observations, structured field notes, and related documentation;
- record and present canvassing routes, visit locations, session timing, and processing status;
- provide organization-authorized dashboards, reports, diagnostics, and operational support;
- provide customer support and respond to requests;
- maintain, secure, monitor, and improve the Services;
- send service-related communications, alerts, and notices; and
- comply with legal obligations and enforce applicable agreements.

We do not use your microphone or location data for advertising purposes, and we do not sell your microphone or location data.

# **4\. Do we disclose any information to outside parties?**

We do not sell, trade, or rent your personal information to third parties for their own marketing purposes. We may disclose personal information as follows:

**Your Organization.**

If you use FieldOps Notes on behalf of an organization, authorized users of that organization may access canvassing-session information, route and location information, doorstep-interaction audio, transcripts, summaries, observations, field notes, visit records, and related reports. Your organization's own policies govern its internal use of this information.

**Service Providers.**

We disclose information to service providers that help operate the Services, such as cloud hosting and storage providers, mapping providers, automated transcription and language-processing providers, customer-support providers, and analytics or monitoring providers. These providers are authorized to use information only as necessary to provide contracted services to us and are subject to applicable contractual and legal restrictions.

**Legal Requirements and Business Transfers.**

We may disclose personal information (i) to comply with law, legal process, or a government request; (ii) to protect against fraud, security threats, or harm; (iii) in connection with an investigation of a complaint, security threat, or suspected illegal activity; or (iv) in connection with a merger, acquisition, financing, reorganization, or sale of assets, in which case personal information may be transferred as part of that transaction.

# **5\. What choices do you have about your personal information?**

**Microphone and Location Permissions.**

You control microphone and location access through your device settings and may deny or revoke those permissions. Because FieldOps Notes relies on microphone and location access to record and document canvassing sessions, denying or revoking either permission may prevent a session from starting, interrupt an active session, or limit the completeness of captured field records.

**Notifications.**

You may adjust push notification preferences in your device or app settings.

**Marketing Communications.**

You may opt out of promotional emails by using the unsubscribe link included in those emails. You cannot opt out of transactional or Service-related messages necessary to operate your account.

**Do Not Track.**

Because there is not currently an industry or legal standard for recognizing or honoring Do Not Track ("DNT") signals, we do not respond to them at this time.

**Updating or Deleting Your Information.**

You may contact us using the information in Section 15 to review, correct, or request deletion of your personal information, subject to legitimate business or legal retention needs.

# **6\. Cookies**

If you visit our website, we and our service providers may use cookies and similar technologies to operate the site, remember your preferences, and analyze site usage. Most browsers let you block or delete cookies; doing so may limit some website functionality. Cookie use does not apply to in-app microphone or location data collection, which is governed separately by your device's app permission settings.

# **7\. Location of this Site**

The Services are hosted and operated in the United States. If you access the Services from outside the United States, you acknowledge and agree that your information may be transferred to, stored, and processed in the United States, where privacy laws may differ from, and may not be as protective as, those in your home jurisdiction.

# **8\. How long do we retain your personal information?**

We retain personal information only for as long as reasonably necessary to provide the Services, satisfy the authorized organization's recordkeeping requirements, maintain security and operational records, resolve disputes, and comply with legal obligations. Retention periods may differ by data category, including account records, audio, transcripts, derived field documentation, precise location information, reports, and system logs. At the end of the applicable retention period, information is deleted, de-identified, or otherwise handled as permitted by law.

# **9\. Third party links**

Occasionally, at our discretion, the Services may include or link to third-party products or services. These third-party sites and services have separate, independent privacy policies, and we have no responsibility or liability for their content or activities. We urge you to read the privacy policies of other websites and services before submitting any information to them.

# **10\. Third party application usage policy**

Where FieldOps Notes integrates with third-party services, including mapping, storage, communications, transcription, or language-processing services, our use and transfer of information received from or sent to those services will follow applicable contractual terms, privacy obligations, and limited-use requirements. We use access controls intended to limit data exchange to information needed for the applicable service function.

# 11. Automated Transcription and Language-Processing Services

FieldOps Notes uses automated systems, including speech-to-text transcription and language-processing systems, to process audio captured during canvassing sessions and to produce transcripts, summaries, sentiments, observations, structured field notes, and related documentation. Where feasible, this processing may use systems operated or controlled by us. We may also use contracted service providers to perform specific transcription, language-processing, storage, or related functions.

Information provided to contracted processing providers may be used only to deliver the requested services to FieldOps Notes and is subject to applicable contractual retention, confidentiality, security, and use restrictions. Those providers may not use FieldOps Notes customer audio or text to train or improve general-purpose models except where FieldOps Notes has expressly authorized that use and provided any notice or consent required by law.

You may contact us at help@structurely.com with questions about the automated processing used by the Services or the service providers involved.

# **12\. How do we protect your information?**

We implement reasonable administrative, technical, and physical safeguards designed to protect personal information — including audio recordings and location data — from unauthorized access, use, or disclosure. This includes encryption in transit, access controls limiting who within our organization can view sensitive data, and training for personnel who handle personal information. Only authorized individuals may access your personal information. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.

# **13\. Your California privacy rights**

Under the California Consumer Privacy Act, as amended by the California Privacy Rights Act (CCPA/CPRA), California residents have certain rights regarding their personal information, including "sensitive personal information" such as precise geolocation and audio recordings, which FieldOps Notes collects as described in this Policy. Pursuant to applicable California law, FieldOps Notes makes the following disclosures regarding the personal information we collect:

 

| Category of Personal Information | Category of Source | Purpose of Collection | Categories of Third Parties to Whom Disclosed |
| :---- | :---- | :---- | :---- |
| Name, email address, phone number, job title, Organization | Directly from you; your Organization | Account creation and administration | Your Organization; hosting/service providers |
| Precise geolocation data (Sensitive Personal Information) | Automatically, via device GPS, with your permission | Dispatch, routing, job-site verification, time tracking | Your Organization; customers (limited, e.g. ETA); mapping/routing providers |
| Audio recordings and transcriptions (Sensitive Personal Information) | Automatically, via device microphone, with your permission | Voice notes, job documentation, transcription | Your Organization; automated transcription and language-processing providers |
| Job and work order data (addresses, service notes, photos, invoices) | Directly from you; customers; your Organization | Providing field service management functionality | Your Organization; hosting providers |
| Device and usage data (device type, OS, app version, IP address, crash logs) | Automatically | Operating, securing, and improving the Services | Analytics and hosting providers |

 

FieldOps Notes has not sold consumers' personal information in the twelve (12) months preceding the effective date of this Policy.

If you are a California resident, you have the right to request:

●      the categories of personal information FieldOps Notes has collected about you;

●      the categories of sources from which your personal information is collected;

●      the business or commercial purpose for collecting your personal information;

●      the categories of third parties with whom FieldOps Notes shares your personal information;

●      the specific pieces of personal information FieldOps Notes has collected about you;

●      deletion of your personal information, subject to certain legal exceptions; and

●      to opt out of the sale or sharing of your personal information (we do not currently sell or share personal information as those terms are defined by the CCPA/CPRA).

California residents also have the right to limit the use and disclosure of sensitive personal information — including precise geolocation data and audio recordings — to purposes necessary to provide the Services. We use sensitive personal information only for the necessary purposes described in this Policy and do not use it to infer characteristics about you.

To submit a request, please contact us at help@structurely.com. FieldOps Notes will not discriminate against you for exercising any rights under the CCPA/CPRA, including by denying goods or services, charging different prices, or providing a different level or quality of service.

# **14\. Do we collect information from children?**

The Services are intended for authorized adult users conducting or managing canvassing activity and are not intended for children under 13. No person under 13 may provide information to or through the Services. We do not knowingly collect personal information from children under 13\. If we learn we have collected or received personal information from a child under 13 without verification of parental consent, we will delete that information. If you believe we might have information from or about a child under 13, please contact us at help@structurely.com.

Pursuant to the CCPA, we will not sell the personal information of any consumer less than 16 years of age, unless the consumer (if between 13 and 16 years of age) or the consumer's parent or guardian (if the consumer is under 13\) has affirmatively authorized the sale of that information.

# **15\. Contacting us**

For any questions, or to request further information regarding this Privacy Policy, please contact us at:

FieldOps Notes, Inc.

2655 N. Airport Rd.

Fort Myers, FL 33907

[help@structurely.com](mailto:help@structurely.com)

 

# **16\. Changes to our Privacy Policy**

It is our policy to post any changes we make to this Privacy Policy on this page and update the effective date above. If we make material changes to how we treat your personal information, we will notify you by email to the primary address you have provided to us and/or through a notice within the Services. You are responsible for ensuring we have an up-to-date, deliverable email address for you, and for periodically reviewing this Policy for changes
